import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { appConfig } from '@/app/config/appConfig';
import { GameClearModal } from '@/components/shared/GameClearModal';
import { isSolvedBoard, moveTile } from '@/lib/sliding-puzzle/board';
import type { DemoBoard, DemoLevel } from '@/types/sliding-puzzle';
import mandalaImage from '@micro-screens/assets/mandala-vector.png';
import { STAGE_THEME_COLORS, type StageId } from '@micro-screens/src';

import type { SoundSystem } from '../hooks/useSoundSystem';
import '../styles/game.css';

import GameBoard from './GameBoard';
import MemorizeOverlay from './MemorizeOverlay';
import RevealOverlay from './RevealOverlay';
import SoundSettings from './SoundSettings';

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${String(r)}, ${String(g)}, ${String(b)}`;
}

interface DemoFlowProps {
  stage: StageId;
  levels: DemoLevel[];
  isPreview?: boolean;
  onComplete: () => void;
  sound?: SoundSystem;
  stopAnimation?: boolean;
  onStopAnimationToggle?: () => void;
}

interface FlatDemoBoard extends DemoBoard {
  levelName: string;
}

type DemoIntroPhase = 'memorizing' | 'shuffle-animation' | 'playing';

const GATHER_START_DELAY_MS = 750;
const GATHER_TRANSITION_MS = 550;
const GATHER_SETTLE_MS = 120;
const SCATTER_TRANSITION_MS = 550;
const SCATTER_SETTLE_MS = 200;

function buildBoards(levels: DemoLevel[]): FlatDemoBoard[] {
  const flat: FlatDemoBoard[] = [];
  for (const lvl of levels)
    for (const b of lvl.boards) flat.push({ ...b, levelName: lvl.levelName });
  return flat;
}

function buildSolvedPieces(gridX: number, gridY: number): number[] {
  const total = gridX * gridY;
  return Array.from({ length: total }, (_, index) => (index === total - 1 ? -1 : index));
}


function getInitialIntroPhase(board: FlatDemoBoard): DemoIntroPhase {
  return board.displayTime > 0 ? 'memorizing' : 'shuffle-animation';
}

export default function DemoFlow({ stage, levels, isPreview = false, onComplete, sound, stopAnimation = false, onStopAnimationToggle = () => {} }: Readonly<DemoFlowProps>) {
  const { t } = useTranslation();
  const theme = STAGE_THEME_COLORS[stage];
  const themeVars = {
    '--game-accent': theme.resultAccent,
    '--game-accent-rgb': hexToRgb(theme.resultAccent),
    '--game-eclipse': theme.eclipse,
    '--game-eclipse-rgb': hexToRgb(theme.eclipse),
  } as CSSProperties;
  const [boards] = useState<FlatDemoBoard[]>(() => buildBoards(levels));
  const [cursor, setCursor] = useState(0);
  const [pieces, setPieces] = useState<number[]>(
    boards.length > 0 ? [...boards[0].initialPieces] : [],
  );
  const [introPhase, setIntroPhase] = useState<DemoIntroPhase>(
    boards.length > 0 ? getInitialIntroPhase(boards[0]) : 'playing',
  );
  const [introShufflePieces, setIntroShufflePieces] = useState<number[] | null>(null);
  const [gatherToCenter, setGatherToCenter] = useState(false);
  const [showBlankImage, setShowBlankImage] = useState(false);
  const [isSolved, setIsSolved] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [levelCompleteBurst, setLevelCompleteBurst] = useState(false);
  const stopAnimationRef = useRef(stopAnimation);
  const soundRef = useRef(sound);
  soundRef.current = sound;
  const solveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const levelCompleteBurstTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable callback for RevealOverlay in preview mode — prevents re-triggering when
  // PreviewApp re-renders (setting showClearModal) and passes a new onComplete reference.
  const previewCompleteRef = useRef(onComplete);
  previewCompleteRef.current = onComplete;
  const stablePreviewRevealComplete = useRef(() => { previewCompleteRef.current(); });

  useEffect(() => {
    if (boards.length === 0) onComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    soundRef.current?.startBackground();
  }, []);

  useEffect(
    () => () => {
      if (solveTimerRef.current) clearTimeout(solveTimerRef.current);
      if (levelCompleteBurstTimerRef.current) clearTimeout(levelCompleteBurstTimerRef.current);
    },
    [],
  );

  useEffect(() => { stopAnimationRef.current = stopAnimation; }, [stopAnimation]);

  useEffect(() => {
    if (stopAnimation) {
      setLevelCompleteBurst(false);
      if (levelCompleteBurstTimerRef.current) {
        clearTimeout(levelCompleteBurstTimerRef.current);
        levelCompleteBurstTimerRef.current = null;
      }
    }
  }, [stopAnimation]);

  useEffect(() => {
    if (!isRevealing) return;
    if (levelCompleteBurstTimerRef.current) clearTimeout(levelCompleteBurstTimerRef.current);
    setLevelCompleteBurst(false);
    if (!stopAnimationRef.current) {
      requestAnimationFrame(() => { setLevelCompleteBurst(true); });
      levelCompleteBurstTimerRef.current = setTimeout(() => {
        setLevelCompleteBurst(false);
        levelCompleteBurstTimerRef.current = null;
      }, 1450);
    }
  }, [isRevealing]);

  const currentBoard = cursor >= 0 && cursor < boards.length ? boards[cursor] : null;

  useEffect(() => {
    if (introPhase !== 'shuffle-animation' || !currentBoard) {
      if (introPhase !== 'shuffle-animation') {
        setIntroShufflePieces(null);
        setGatherToCenter(false);
        setShowBlankImage(false);
      }
      return;
    }

    const { gridSize } = currentBoard;
    const solvedPieces = buildSolvedPieces(gridSize.x, gridSize.y);
    const timers: Array<ReturnType<typeof setTimeout>> = [];

    setIntroShufflePieces(solvedPieces);
    setGatherToCenter(false);
    setShowBlankImage(true);

    const scatterAt = GATHER_START_DELAY_MS + GATHER_TRANSITION_MS + GATHER_SETTLE_MS;
    const completeAt = scatterAt + SCATTER_TRANSITION_MS + SCATTER_SETTLE_MS;

    timers.push(
      setTimeout(() => { setGatherToCenter(true); }, GATHER_START_DELAY_MS),
      setTimeout(() => { setGatherToCenter(false); setIntroShufflePieces(pieces); setShowBlankImage(false); }, scatterAt),
      setTimeout(() => { setIntroPhase('playing'); }, completeAt),
    );

    return () => {
      timers.forEach((timer) => { clearTimeout(timer); });
    };
  }, [currentBoard, introPhase, pieces]);

  const advanceToNext = useCallback(() => {
    const next = cursor + 1;
    if (next >= boards.length) {
      onComplete();
      return;
    }
    const nb = boards[next];
    setCursor(next);
    setPieces([...nb.initialPieces]);
    setIsSolved(false);
    setIsRevealing(false);
    setIntroPhase(getInitialIntroPhase(nb));
  }, [boards, cursor, onComplete]);

  const handleTileClick = useCallback(
    (slot: number) => {
      if (isSolved || introPhase !== 'playing' || !currentBoard) return;
      const result = moveTile(pieces, slot, currentBoard.gridSize.x);
      if (!result.moved) {
        soundRef.current?.playWrongStep();
        return;
      }
      soundRef.current?.playWhoosh();
      setPieces(result.board);
      if (isSolvedBoard(result.board)) {
        setIsSolved(true);
        setIsRevealing(true);
        soundRef.current?.playSuccess();
      }
    },
    [currentBoard, introPhase, isSolved, pieces],
  );

  const autoSolve = useCallback(() => {
    const n = pieces.length;
    const columns = currentBoard?.gridSize.x ?? 0;
    const nextPieces = Array.from({ length: n }, (_, i) => (i === n - 1 ? -1 : i));

    // To leave exactly 3 moves left, we perform 3 valid "backwards" swaps.
    // Path: n-1 (Start) -> n-2 (Left) -> n-2-cols (Up) -> n-1-cols (Right)
    if (n >= columns * 2 && columns >= 2) {
      const i1 = n - 1;
      const i2 = n - 2;
      const i3 = i2 - columns;
      const i4 = i3 + 1;

      [nextPieces[i1], nextPieces[i2]] = [nextPieces[i2], nextPieces[i1]];
      [nextPieces[i2], nextPieces[i3]] = [nextPieces[i3], nextPieces[i2]];
      [nextPieces[i3], nextPieces[i4]] = [nextPieces[i4], nextPieces[i3]];
    } else if (n >= 2) {
      [nextPieces[n - 1], nextPieces[n - 2]] = [nextPieces[n - 2], nextPieces[n - 1]];
    }

    setPieces(nextPieces);
    soundRef.current?.playTap();
    // We no longer automatically trigger the solved state or the next board timer.
    // The user must click the last tile manually.
  }, [currentBoard?.gridSize.x, pieces.length]);

  if (!currentBoard) return null;

  const boardObj = {
    sessionBoardId: '',
    id: currentBoard.id,
    roundNumber: cursor + 1,
    gridSize: currentBoard.gridSize,
    fullImageUrl: currentBoard.fullImageUrl,
    displayTime: currentBoard.displayTime,
    pieces: currentBoard.initialPieces,
  };

  const isLastBoard = cursor + 1 >= boards.length;
  const isMemorizing = introPhase === 'memorizing';
  const isShuffleAnimation = introPhase === 'shuffle-animation';
  const solvedPieces = buildSolvedPieces(currentBoard.gridSize.x, currentBoard.gridSize.y);
  const boardDisplayPieces =
    isMemorizing
      ? solvedPieces
      : isShuffleAnimation
        ? (introShufflePieces ?? solvedPieces)
        : pieces;

  return (
    <main className={`puzzle-shell${levelCompleteBurst && !stopAnimation ? ' puzzle-shell--level-complete' : ''} flex flex-col h-screen overflow-hidden select-none`} style={themeVars}>
      <div className="background-layer" style={{ backgroundImage: `url(${mandalaImage})` }} />
      {levelCompleteBurst && !stopAnimation && <div className="puzzle-level-complete-burst" aria-hidden="true" />}
      <div
        className="puzzle-shell__top-glow"
        style={{
          background: `linear-gradient(${theme.eclipse}, ${theme.eclipse}52)`,
          boxShadow: `0 0 108px ${theme.eclipse}b8`,
        }}
      />

      <div className="h-[100dvh] flex flex-col justify-between pt-[30px] pb-[15px] lg:py-[32px]">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <header className="puzzle-header">
          {/* Left: "Demo"/"Preview" label with pulsing dot */}
          <div className="font-semibold text-[16px] md:text-[18px] tracking-[1px] tracking-[0.16px] text-white">{isPreview ? t('game.preview') : t('game.demo')}</div>

          {/* Right: "Done" skip pill + settings — hidden in preview mode */}
          {!isPreview && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                type="button"
                className="puzzle-demo-skip"
                onClick={onComplete}
                aria-label="Skip demo"
                disabled={showCompletionModal}
              >
                <span className="puzzle-demo-skip__inner">{t('game.done')}</span>
              </button>
              {sound && <SoundSettings sound={sound} stopAnimation={stopAnimation} onStopAnimationToggle={onStopAnimationToggle} />}
            </div>
          )}
        </header>

        {/* <h2 className={`font-semibold text-[18px] leading-[32px] tracking-[0.16px] text-center text-[#9E9BA4] h-[34px] mt-[25px] mb-0 absolute top-[90px] left-1/2 -translate-x-1/2 font-heading-sans ${currentBoard.gridSize.x === 2 && currentBoard.gridSize.y === 3 ? ' hidden sm:block' : ''}`}>
          {levelCompleteBurst && t('game.nextRound')}
        </h2> */}

        {/* ── Playfield ──────────────────────────────────────────────────── */}
        <div className="puzzle-playfield flex-1 relative">
          {/* Board */}
          <div className="absolute inset-0 flex items-center justify-center px-4 pb-16">
            <div
              style={{
                width: '100%',
                maxWidth: '420px',
                aspectRatio: `${String(currentBoard.gridSize.x)} / ${String(currentBoard.gridSize.y)}`,
                maxHeight: '100%',
              }}
            >
              <div className="puzzle-board-card" style={{ height: '100%' }}>
                <GameBoard
                  board={boardObj}
                  pieces={boardDisplayPieces}
                  disabled={
                    isSolved ||
                    isMemorizing ||
                    isShuffleAnimation ||
                    isRevealing
                  }
                  isSolved={isSolved}
                  shuffleMode={isShuffleAnimation}
                  gatherToCenter={gatherToCenter}
                  showBlankImage={showBlankImage}
                  revealMode={isMemorizing}
                  onTileClick={handleTileClick}
                />

                {isMemorizing && (
                  <MemorizeOverlay
                    fullImageUrl={currentBoard.fullImageUrl}
                    gridX={currentBoard.gridSize.x}
                    gridY={currentBoard.gridSize.y}
                    displayTime={currentBoard.displayTime}
                    onComplete={() => {
                      setIntroPhase('shuffle-animation');
                    }}
                  />
                )}

                {isRevealing && (
                  <RevealOverlay
                    fullImageUrl={currentBoard.fullImageUrl}
                    gridX={currentBoard.gridSize.x}
                    gridY={currentBoard.gridSize.y}
                    onComplete={isPreview ? stablePreviewRevealComplete.current : () => {
                      if (isLastBoard) {
                        setShowCompletionModal(true);
                      } else {
                        solveTimerRef.current = setTimeout(advanceToNext, 1200);
                      }
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Board progress dots */}
          {!isPreview && (
            <div className="puzzle-round-dots" aria-label="Demo progress">
              {boards.map((_, i) => {
                let modifier = '';
                if (i < cursor) modifier = 'puzzle-round-dot--done';
                else if (i === cursor) modifier = 'puzzle-round-dot--current';
                return (
                  <span key={i} className={`puzzle-round-dot${modifier ? ` ${modifier}` : ''}`} />
                );
              })}
            </div>
          )}

          {/* Hint banner */}
          {introPhase === 'playing' && !isSolved && (
            <div className="puzzle-hint-banner">{t('game.hintBanner')}</div>
          )}
        </div>

        {/* ── Dev auto-solve ──────────────────────────────────────────────── */}
        {appConfig.features.devtools && introPhase === 'playing' && !isSolved && (
          <div className="puzzle-bottom-bar">
            <button type="button" className="puzzle-btn-ghost" onClick={autoSolve}>
              {t('game.autoSolve')}
            </button>
          </div>
        )}
      </div>

      {showCompletionModal && (
        <GameClearModal
          title={t('game.demoCleared')}
          accentColor={theme.resultAccent}
          eclipseColor={theme.eclipse}
          onConfirm={onComplete}
        />
      )}
    </main>
  );
}
